import type { AnyAction, Dispatch } from '@reduxjs/toolkit';
import { EMPTY_OBJECT } from 'constants/defaults';
import { memoize } from 'lodash-es';
import type { HourState, HourStatePermission } from 'redux/modules/hourStates.schema';
import type { IState } from 'redux/state';
import { defaultState } from './hourStates.constants';
import { injectHours } from './hours.types';

export const createInvalidState = memoize(
  (id: number): HourState => ({
    ...defaultState,
    id,
    name: `<unknown (id: ${id})>`,
  })
);

export const synthesizeStatePermissionForRole = (
  roleId: number,
  stateId = -1
): HourStatePermission => ({
  id: -1,
  created_at: 0,
  updated_at: 0,
  operation: 'manage',
  state_id: stateId,
  role_id: roleId,
  project_lead: false,
  team_lead: false,
});

export const synthesizeStatePermissionForTeamLead = (stateId = -1): HourStatePermission => ({
  id: -1,
  created_at: 0,
  updated_at: 0,
  operation: 'manage',
  state_id: stateId,
  role_id: -1,
  project_lead: false,
  team_lead: true,
});

/**
 * Updates any local hours that have this state to keep them consistent with the state's
 * locked/billed and permission settings.
 */
export const updateHoursWithSavedState = (
  savedState: HourState,
  dispatch: Dispatch<AnyAction>,
  state: IState
) => {
  const hoursWithState = Object.values(state.hours.entities.hour ?? EMPTY_OBJECT)
    .filter(s => s.state?.id === savedState.id)
    .map(h => ({ ...h, state: savedState }));

  if (hoursWithState.length) {
    dispatch(injectHours(hoursWithState));
  }
};

/**
 * Updates any local hours that had the deleted state to instead have the default state.
 */
export const updateHoursWithDeletedState = (
  deletedStateId: number,
  dispatch: Dispatch<AnyAction>,
  state: IState
) => {
  const hoursWithState = Object.values(state.hours.entities.hour ?? EMPTY_OBJECT)
    .filter(s => s.state?.id === deletedStateId)
    .map(h => ({ ...h, state: null }));

  if (hoursWithState.length) {
    dispatch(injectHours(hoursWithState));
  }
};
